import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface KeyRowProps {
  icon?: React.ReactNode
  title: React.ReactNode
  /** e.g. "EM-0614 · 212 OPENS · GUESTS CAN ADD PHOTOS" */
  meta: React.ReactNode
  revokeLabel?: string
  onRevoke?: () => void
}

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  padding: '10px 0',
  borderTop: '1px solid var(--ds-border)',
})

const IconTile = styled(Box)({
  width: 28,
  height: 28,
  flexShrink: 0,
  borderRadius: 9,
  background: 'var(--ds-hover)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
})

const Title = styled('div')({
  fontSize: 12.5,
  fontWeight: 640,
  color: 'var(--ds-ink)',
})

const Meta = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.06em',
  color: 'var(--ds-ink-subtle)',
  marginTop: 1,
})

const RevokeButton = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  marginLeft: 'auto',
  flexShrink: 0,
  cursor: 'pointer',
  fontSize: 11.5,
  fontWeight: 600,
  color: 'var(--ds-ink-subtle)',
  transition: 'color var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': {
    color: 'var(--ds-danger)',
  },
})

/** Single issued-key row — icon, name + mono meta, right-aligned revoke action. */
export function KeyRow({ icon = '⚿', title, meta, revokeLabel = 'Revoke', onRevoke }: KeyRowProps) {
  return (
    <Row>
      <IconTile aria-hidden>{icon}</IconTile>
      <Box sx={{ minWidth: 0 }}>
        <Title>{title}</Title>
        <Meta>{meta}</Meta>
      </Box>
      {onRevoke && (
        <RevokeButton type='button' onClick={onRevoke}>
          {revokeLabel}
        </RevokeButton>
      )}
    </Row>
  )
}

export interface KeysListProps extends Omit<BoxProps, 'children'> {
  children: React.ReactNode
}

/** Vertical list container for `KeyRow`s — the share sheet's "Active keys" section. */
export function KeysList({ children, ...rest }: KeysListProps) {
  return <Box {...rest}>{children}</Box>
}
